import {
  Box,
  Card,
  CardMedia,
  FormControl,
  Grid,
  LinearProgress,
  MenuItem,
  Popover,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import DateRangePickerDay, { DateRangePickerDayProps } from '@mui/lab/DateRangePickerDay';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import StaticDateRangePicker from '@mui/lab/StaticDateRangePicker';
import { MouseEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Distributor, getDistributorsListForFilters } from '../../redux-store/actions';
import { getOnboardingDashboard } from '../../redux-store/actions/application';
import { getFundsList } from '../../redux-store/actions/funds';
import {
  OnboardingDashboardResponse,
  OnboardingFilterType,
} from '../../redux-store/types/api-types';
import { FundProps, GetFundsListResponseBody } from '../../redux-store/types/funds';
import { RootStateType } from '../../redux-store/reducers';
import { ONBOARDING_DASHBOARD_STATUS, USER_ROLES } from '../../utils/constant';
import { formatShortDate } from '../../utils/date';
import { formatCurrency } from '../../utils/utilityFunctions';

type ApplicationTab = 'completed' | 'pipeline';

const PIPELINE_STATUS_ORDER = [
  'draft',
  'review',
  'sent_to_investor',
  'sent_to_im',
  'sent_to_trustee',
  'expired',
  'rejected',
];

const filterSelectSx = {
  height: '36px',
  borderRadius: '74px',
  backgroundColor: '#FFFFFF',
  fontFamily: 'Roboto, sans-serif',
  fontSize: '14px',
  width: '100%',
  minWidth: 0,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#D9DEE5',
    borderRadius: '74px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#B8C0CB',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#2057A6',
    borderWidth: '1px',
  },
  '&.Mui-focused, &:focus-within, &:focus-visible': {
    outline: '3px solid #2057A6 !important',
    outlineOffset: '2px !important',
    borderRadius: '74px !important',
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    height: '36px',
    boxSizing: 'border-box',
    py: 0,
    minWidth: 0,
    overflow: 'hidden',
    paddingRight: '32px !important',
  },
  '& .MuiSelect-select:focus': {
    outline: 'none',
    backgroundColor: 'transparent',
  },
};

const RangeDay = (props: DateRangePickerDayProps<Date>) => (
  <DateRangePickerDay
    {...props}
    sx={{
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlight': {
        backgroundColor: '#E8ECF0',
      },
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart, &.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd':
        {
          backgroundColor: 'transparent',
        },
      '& .MuiDateRangePickerDay-day': {
        fontSize: 13,
        fontWeight: 500,
        color: '#5F6B7A',
        borderRadius: '4px',
        '&.Mui-selected': {
          backgroundColor: '#2057A6',
          color: '#FFFFFF',
          fontWeight: 600,
          '&:hover, &:focus': {
            backgroundColor: '#2057A6',
          },
        },
        '&.MuiDateRangePickerDay-dayInsideRangeInterval': {
          color: '#293139',
          fontWeight: 600,
        },
      },
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart .MuiDateRangePickerDay-day': {
        borderTopLeftRadius: '4px',
        borderBottomLeftRadius: '4px',
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      },
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd .MuiDateRangePickerDay-day': {
        borderTopRightRadius: '4px',
        borderBottomRightRadius: '4px',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      },
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd .MuiDateRangePickerDay-day':
        {
          borderRadius: '4px',
        },
    }}
  />
);

const formatAmountWithSymbol = (amount: number, currency: string): string => {
  const symbol = currency === 'USD' ? '$' : '₹';
  return `${symbol} ${formatCurrency(amount || 0, currency)}`;
};

export default function OnboardingdDashboard(): JSX.Element {
  const dispatch = useDispatch();
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const isDistributorUser = [USER_ROLES.DISTRIBUTOR, USER_ROLES.SUBDISTRIBUTOR].includes(
    role as USER_ROLES
  );
  const [dashboardData, setDashboardData] = useState<OnboardingDashboardResponse | null>(null);
  const [isOnboardingSummaryLoading, setIsOnboardingSummaryLoading] = useState(false);
  const currency = 'INR';
  const [activeTab, setActiveTab] = useState<ApplicationTab>('completed');
  const [schemeId, setSchemeId] = useState('');
  const [distributorId, setDistributorId] = useState('');
  const [funds, setFunds] = useState<FundProps[]>([]);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    let componentIsAlive = true;
    (async function () {
      try {
        const { funds: fundsList } = (await dispatch(
          getFundsList()
        )) as unknown as GetFundsListResponseBody;
        const distributorsList = isDistributorUser
          ? []
          : ((await dispatch(getDistributorsListForFilters())) as unknown as Distributor[]);
        if (!componentIsAlive) return;
        setFunds(fundsList || []);
        setDistributors(distributorsList || []);
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
    return () => {
      componentIsAlive = false;
    };
  }, []);

  useEffect(() => {
    let componentIsAlive = true;
    (async function () {
      try {
        setIsOnboardingSummaryLoading(true);
        const params: Partial<OnboardingFilterType> = {};
        if (schemeId) params.schemeId = schemeId;
        if (distributorId) params.distributorId = Number(distributorId);
        if (fromDate) {
          const startOfDay = new Date(fromDate);
          startOfDay.setHours(0, 0, 0, 0);
          params.from = startOfDay.toISOString();
        }
        if (toDate) {
          const endOfDay = new Date(toDate);
          endOfDay.setHours(23, 59, 59, 999);
          params.to = endOfDay.toISOString();
        }
        const data = (await dispatch(
          getOnboardingDashboard(Object.keys(params).length ? params : undefined)
        )) as unknown as OnboardingDashboardResponse;
        if (!componentIsAlive) {
          return;
        }
        setDashboardData(data);
      } catch (e) {
        console.error((e as Error).message);
      } finally {
        if (componentIsAlive) {
          setIsOnboardingSummaryLoading(false);
        }
      }
    })();
    return () => {
      componentIsAlive = false;
    };
  }, [schemeId, distributorId, fromDate, toDate]);

  const dateLabel =
    fromDate || toDate
      ? `${fromDate ? formatShortDate(fromDate) : 'From'} - ${
          toDate ? formatShortDate(toDate) : 'To'
        }`
      : 'Date Range';

  const totalAmount = dashboardData?.summary?.totalValue?.commitmentAmount || 0;
  const totalCount = dashboardData?.summary?.totalValue?.count || 0;
  const processedAmount = dashboardData?.summary?.processed?.commitmentAmount || 0;
  const processedCount = dashboardData?.summary?.processed?.count || 0;
  const pipelineAmount = dashboardData?.summary?.pipeline?.commitmentAmount || 0;
  const pipelineCount = dashboardData?.summary?.pipeline?.count || 0;
  const failedAmount = dashboardData?.summary?.failed?.commitmentAmount || 0;
  const failedCount = dashboardData?.summary?.failed?.count || 0;

  const completedStatus = dashboardData?.applicationStatus?.completed;
  const pipelineStatusSummary = dashboardData?.applicationStatus?.pipeline;
  const completedAmount = completedStatus?.completedCommitmentAmount || 0;
  const completedCount = completedStatus?.completedCount || 0;
  const pipelineStatusAmount = pipelineStatusSummary?.pipelineCommitmentAmount || 0;
  const pipelineStatusCount = pipelineStatusSummary?.pipelineCount || 0;
  const activeStatus = activeTab === 'completed' ? completedStatus : pipelineStatusSummary;
  const individualCount = activeStatus?.individualCount || 0;
  const nonIndividualCount = activeStatus?.nonIndividualCount || 0;
  const individualAmount = activeStatus?.individualCommitmentAmount || 0;
  const nonIndividualAmount = activeStatus?.nonIndividualCommitmentAmount || 0;
  const poaCount = activeStatus?.poaCount || 0;
  const poaAmount = activeStatus?.poaCommitmentAmount || 0;
  const nriCount = activeStatus?.nriCount || 0;
  const nriAmount = activeStatus?.nriCommitmentAmount || 0;
  const nriPoaCount = activeStatus?.nriPoaCount || 0;
  const nriPoaAmount = activeStatus?.nriPoaCommitmentAmount || 0;
  const applicationTypeBars = [
    {
      label: 'Individual (Resident)',
      count: individualCount,
      amount: individualAmount,
    },
    {
      label: 'Non-Individual',
      count: nonIndividualCount,
      amount: nonIndividualAmount,
    },
    {
      label: 'Individual (POA)',
      count: poaCount,
      amount: poaAmount,
    },
    {
      label: 'Individual (NRI)',
      count: nriCount,
      amount: nriAmount,
    },
    {
      label: 'Individual (NRI-POA)',
      count: nriPoaCount,
      amount: nriPoaAmount,
    },
  ];
  const maxBarCount = Math.max(...applicationTypeBars.map((row) => row.count), 1);

  const displayPipelineData = PIPELINE_STATUS_ORDER.map((stage) => {
    const matched = dashboardData?.pipelineStatus?.find((item) => item.stage === stage);
    return {
      status: stage,
      count: matched?.count || 0,
      commitmentAmount: matched?.commitmentAmount || 0,
    };
  });

  const metricCards = [
    {
      label: 'TOTAL VALUE',
      amount: totalAmount,
      count: totalCount,
      bg: 'linear-gradient(95.65deg, #E8F1FF 1.71%, #D2E4FF 97.94%)',
      color: '#1450A7',
      icon: '/images/total-value.svg',
    },
    {
      label: 'PROCESSED',
      amount: processedAmount,
      count: processedCount,
      bg: 'linear-gradient(95.65deg, #E0FCE9 1.71%, #B3F3D0 97.94%)',
      color: '#004B50',
      icon: '/images/completed.svg',
    },
    {
      label: 'PIPELINE',
      amount: pipelineAmount,
      count: pipelineCount,
      bg: 'linear-gradient(95.65deg, #FFF8E1 1.71%, #FFECB8 97.94%)',
      color: '#7A4100',
      icon: '/images/pipeline.svg',
    },
    {
      label: 'FAILED',
      amount: failedAmount,
      count: failedCount,
      bg: 'linear-gradient(135deg, #FCE8E8 0%, #F9D4D4 100%)',
      color: '#B42318',
      icon: '/images/failed.svg',
    },
  ];

  return (
    <>
      <Grid item xs={12} sx={{ minWidth: 0, maxWidth: '100%', overflow: 'visible' }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'center',
            width: '100%',
            minWidth: 0,
            py: 1,
            px: 0.75,
            overflow: 'visible',
          }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans serif',
              fontWeight: 500,
              fontSize: 16,
              color: '#111927',
              flexShrink: 0,
            }}>
            Filters:
          </Typography>

          <FormControl
            size="small"
            sx={{
              flex: '1 1 140px',
              minWidth: 0,
              maxWidth: { xs: '100%', sm: 180 },
              overflow: 'visible',
            }}>
            <Select
              displayEmpty
              value={schemeId}
              onChange={(event: SelectChangeEvent) => setSchemeId(event.target.value)}
              IconComponent={KeyboardArrowDownIcon}
              inputProps={{ 'aria-label': 'Filter by scheme' }}
              sx={filterSelectSx}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: '#6C737F',
                        fontFamily: 'Roboto, sans-serif',
                      }}>
                      Scheme
                    </Typography>
                  );
                }
                const fund = funds.find((item) => String(item.id) === String(selected));
                return (
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: '#293139',
                      fontFamily: 'Roboto, sans-serif',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                    {fund?.schemeName || 'Scheme'}
                  </Typography>
                );
              }}>
              <MenuItem value="">
                <Typography sx={{ fontSize: 14, color: '#293139' }}>All Schemes</Typography>
              </MenuItem>
              {funds.map((fund) => (
                <MenuItem key={fund.id} value={String(fund.id)}>
                  {fund.schemeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {!isDistributorUser && (
            <FormControl
              size="small"
              sx={{
                flex: '1 1 140px',
                minWidth: 0,
                maxWidth: { xs: '100%', sm: 180 },
                overflow: 'visible',
              }}>
              <Select
                displayEmpty
                value={distributorId}
                onChange={(event: SelectChangeEvent) => setDistributorId(event.target.value)}
                IconComponent={KeyboardArrowDownIcon}
                inputProps={{ 'aria-label': 'Filter by distributor' }}
                sx={filterSelectSx}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: '#6C737F',
                          fontFamily: 'Roboto, sans-serif',
                        }}>
                        Distributor
                      </Typography>
                    );
                  }
                  const distributor = distributors.find(
                    (item) => String(item.id) === String(selected)
                  );
                  return (
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: '#293139',
                        fontFamily: 'Roboto, sans-serif',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                      {distributor?.name || distributor?.user?.firstName || 'Distributor'}
                    </Typography>
                  );
                }}>
                <MenuItem value="">
                  <Typography sx={{ fontSize: 14, color: '#293139' }}>All Distributors</Typography>
                </MenuItem>
                {distributors.map((distributor) => (
                  <MenuItem key={distributor.id} value={String(distributor.id)}>
                    {distributor.name || distributor.user?.firstName || 'N/A'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box
            sx={{
              flex: { xs: '1 1 100%', sm: '0 0 auto' },
              minWidth: { xs: 0, sm: 280 },
              width: { xs: '100%', sm: 'auto' },
              maxWidth: { xs: '100%', sm: 320 },
              position: 'relative',
              overflow: 'visible',
            }}>
            <Box
              component="button"
              type="button"
              aria-label="Select date range filter"
              onClick={(e: MouseEvent<HTMLButtonElement>) => setDateAnchorEl(e.currentTarget)}
              sx={{
                width: '100%',
                minWidth: { xs: 0, sm: 280 },
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                px: 1.5,
                overflow: 'visible',
                boxSizing: 'border-box',
                border: '1px solid #D9DEE5',
                borderRadius: '74px',
                backgroundColor: '#FFFFFF',
                cursor: 'pointer',
                fontFamily: 'Roboto, sans-serif',
                fontSize: 14,
                fontWeight: 500,
                color: fromDate || toDate ? '#293139' : '#6C737F',
                textAlign: 'left',
                transition: 'border-color 0.2s ease',
                '&:hover': {
                  borderColor: '#B8C0CB',
                },
                '&:focus': {
                  outline: '3px solid #2057A6',
                  outlineOffset: '2px',
                  borderColor: '#2057A6',
                  borderRadius: '74px',
                },
              }}>
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: 12, sm: 14 },
                  fontWeight: 500,
                  color: fromDate || toDate ? '#293139' : '#6C737F',
                  whiteSpace: 'nowrap',
                }}>
                {dateLabel}
              </Typography>
              <KeyboardArrowDownIcon sx={{ fontSize: 20, color: '#6C737F', flexShrink: 0 }} />
            </Box>
            <Popover
              open={Boolean(dateAnchorEl)}
              anchorEl={dateAnchorEl}
              onClose={() => setDateAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  px: 2,
                  pt: 1.5,
                  pb: 1.5,
                  width: {
                    xs: 300,
                    sm: 320,
                  },
                  borderRadius: '10px',
                  boxShadow: '0px 8px 24px rgba(26, 43, 60, 0.12)',
                  border: '1px solid #E8ECF0',
                  overflow: 'hidden',
                },
              }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box
                  sx={{
                    '& .MuiPickerStaticWrapper-root': {
                      minWidth: 'unset',
                    },
                    '& .MuiCalendarPicker-root': {
                      width: '100%',
                      maxHeight: 'unset',
                    },
                    '& .MuiPickersCalendarHeader-root': {
                      paddingLeft: 0,
                      paddingRight: 0,
                    },
                    '& .MuiPickersCalendarHeader-label': {
                      fontSize: 15,
                      fontWeight: 700,
                      color: '#293139',
                    },
                    '& .MuiDayPicker-header': {
                      justifyContent: 'space-between',
                    },
                    '& .MuiDayPicker-weekDayLabel': {
                      fontSize: 12,
                      color: '#9AA3AF',
                      fontWeight: 500,
                      width: 36,
                      height: 32,
                    },
                    '& .MuiDayPicker-weekContainer': {
                      justifyContent: 'space-between',
                      margin: 0,
                    },
                    '& .MuiDayPicker-slideTransition, & .MuiPickersSlideTransition-root, & .PrivatePickersSlideTransition-root':
                      {
                        minHeight: '240px !important',
                      },
                    '& .MuiDateRangePickerDay-root': {
                      width: 36,
                      height: 36,
                      margin: 0,
                    },
                  }}>
                  <StaticDateRangePicker
                    displayStaticWrapperAs="desktop"
                    calendars={1}
                    allowSameDateSelection
                    value={[fromDate, toDate]}
                    maxDate={new Date()}
                    onChange={(newValue) => {
                      const [start, end] = newValue;
                      setFromDate(start);
                      setToDate(end);
                    }}
                    renderDay={(_day, dateRangePickerDayProps) => (
                      <RangeDay {...dateRangePickerDayProps} />
                    )}
                    renderInput={(startProps, endProps) => (
                      <>
                        <TextField {...startProps} sx={{ display: 'none' }} />
                        <TextField {...endProps} sx={{ display: 'none' }} />
                      </>
                    )}
                  />
                </Box>
              </LocalizationProvider>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 0.5,
                }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => {
                    setFromDate(null);
                    setToDate(null);
                    setDateAnchorEl(null);
                  }}
                  sx={{
                    border: 'none',
                    background: 'transparent',
                    color: '#2057A6',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: 'Roboto, sans-serif',
                    p: 0,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}>
                  Clear dates
                </Box>
              </Box>
            </Popover>
          </Box>
        </Box>
      </Grid>

      {isOnboardingSummaryLoading && (
        <Grid item xs={12}>
          <Box sx={{ width: '100%', mb: 2 }}>
            <LinearProgress />
          </Box>
        </Grid>
      )}

      <Grid item xs={12} sx={{ minWidth: 0, maxWidth: '100%' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: { xs: 1.5, sm: 2 },
            width: '100%',
          }}>
          {metricCards.map((card) => (
            <Box
              key={card.label}
              sx={{
                backgroundImage: `url(/images/card-background.svg), ${card.bg}`,
                backgroundRepeat: 'no-repeat, no-repeat',
                backgroundPosition: 'center, center',
                backgroundSize: 'cover, cover',
                borderRadius: 3,
                p: { xs: 1.75, sm: 2, lg: 2.5 },
                minHeight: { xs: 110, sm: 120, lg: 140 },
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                width: '100%',
                minWidth: 0,
                height: '100%',
                boxSizing: 'border-box',
              }}>
              <Typography
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: 12, sm: 13, lg: 14 },
                  lineHeight: 1.2,
                  letterSpacing: 0,
                  color: '#000',
                  textTransform: 'uppercase',
                }}>
                {card.label}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: { xs: 1, sm: 1.5 },
                  width: '100%',
                  minWidth: 0,
                  mt: 1,
                }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: { xs: 18, sm: 20, lg: 22 },
                      fontWeight: 700,
                      color: card.color,
                      lineHeight: 1.2,
                      wordBreak: 'break-word',
                    }}>
                    {formatAmountWithSymbol(card.amount, currency)}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: { xs: 11, sm: 12, lg: 13 },
                      fontWeight: 400,
                      color: card.color,
                      mt: 0.5,
                      lineHeight: 1.3,
                    }}>
                    of {card.count} applications
                  </Typography>
                </Box>
                <Box
                  component="img"
                  src={card.icon}
                  alt=""
                  sx={{
                    width: { xs: 28, sm: 32, lg: 36 },
                    height: { xs: 28, sm: 32, lg: 36 },
                    flexShrink: 0,
                    display: 'block',
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Grid>

      <Grid item xs={12} lg={7} sx={{ minWidth: 0, maxWidth: '100%' }}>
        <Card
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            border: '1px solid #E8ECF0',
            height: '100%',
            background: '#fff',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}>
          <Typography
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontStyle: 'normal',
              fontSize: '20px',
              fontWeight: 600,
              lineHeight: 1,
              letterSpacing: 0,
              color: '#111927',
              mb: 3,
            }}>
            Application Status
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              mb: 3,
              p: 0.75,
              borderRadius: 2,
              background: '#F4F6F8',
              flexDirection: { xs: 'column', lg: 'row' },
              flexWrap: 'wrap',
              minWidth: 0,
              width: '100%',
            }}>
            <Box
              component="button"
              type="button"
              aria-pressed={activeTab === 'completed'}
              onClick={() => setActiveTab('completed')}
              sx={{
                flex: { xs: '1 1 auto', lg: '1 1 0' },
                minWidth: 0,
                maxWidth: '100%',
                width: { xs: '100%', lg: 'auto' },
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                borderRadius: 2,
                p: { xs: 1.5, sm: 2 },
                background: activeTab === 'completed' ? '#fff' : 'transparent',
                boxShadow: activeTab === 'completed' ? '0 2px 8px rgba(26, 43, 60, 0.08)' : 'none',
                transition: 'all 0.2s ease',
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: activeTab === 'completed' ? '#E8F8F7' : '#E8ECF0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                  <CardMedia
                    component="img"
                    src="/images/status1.svg"
                    alt=""
                    sx={{ width: 20, height: 18 }}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 400,
                      fontSize: 14,
                      lineHeight: '20px',
                      color: '#6C737F',
                    }}>
                    Completed Applications
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 600,
                      fontSize: 20,
                      lineHeight: 1.2,
                      color: '#111927',
                      wordBreak: 'break-word',
                    }}>
                    {formatAmountWithSymbol(completedAmount, currency)}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 400,
                      fontSize: 14,
                      lineHeight: '20px',
                      color: '#6C737F',
                    }}>
                    of {completedCount} applications
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              component="button"
              type="button"
              aria-pressed={activeTab === 'pipeline'}
              onClick={() => setActiveTab('pipeline')}
              sx={{
                flex: { xs: '1 1 auto', lg: '1 1 0' },
                minWidth: 0,
                maxWidth: '100%',
                width: { xs: '100%', lg: 'auto' },
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                borderRadius: 2,
                p: { xs: 1.5, sm: 2 },
                background: activeTab === 'pipeline' ? '#fff' : 'transparent',
                boxShadow: activeTab === 'pipeline' ? '0 2px 8px rgba(26, 43, 60, 0.08)' : 'none',
                transition: 'all 0.2s ease',
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: activeTab === 'pipeline' ? '#E8F8F7' : '#E8ECF0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                  <CardMedia
                    component="img"
                    src="/images/status2.svg"
                    alt=""
                    sx={{ width: 20, height: 20 }}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 400,
                      fontSize: 14,
                      lineHeight: '20px',
                      color: '#6C737F',
                    }}>
                    Pipeline Applications
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 600,
                      fontSize: 20,
                      lineHeight: 1.2,
                      color: '#111927',
                      wordBreak: 'break-word',
                    }}>
                    {formatAmountWithSymbol(pipelineStatusAmount, currency)}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 400,
                      fontSize: 14,
                      lineHeight: '20px',
                      color: '#6C737F',
                    }}>
                    of {pipelineStatusCount} applications
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: { xs: 2, sm: 6 } }}>
            {applicationTypeBars.map((row) => (
              <Box
                key={row.label}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: { xs: 1, sm: 2 },
                  minWidth: 0,
                  width: '100%',
                }}>
                <Typography
                  sx={{
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 400,
                    fontSize: 14,
                    lineHeight: '20px',
                    letterSpacing: 0,
                    textAlign: { xs: 'left', sm: 'right' },
                    color: '#6C737F',
                    minWidth: { xs: 0, sm: 160 },
                    flexShrink: 0,
                  }}>
                  {row.label}
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    height: 32,
                    minWidth: 0,
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                  {row.count > 0 && (
                    <Box
                      sx={{
                        width: `${Math.max((row.count / maxBarCount) * 70, 8)}%`,
                        minWidth: 48,
                        maxWidth: '70%',
                        height: '100%',
                        borderRadius: 20,
                        background: '#0072BC',
                        display: 'flex',
                        alignItems: 'center',
                        px: 1.5,
                        transition: 'width 0.3s ease',
                        flexShrink: 0,
                      }}>
                      <Typography
                        sx={{
                          fontFamily: 'Roboto, sans-serif',
                          fontWeight: 600,
                          fontSize: 14,
                          lineHeight: '20px',
                          letterSpacing: 0,
                          color: '#fff',
                          whiteSpace: 'nowrap',
                        }}>
                        {row.count}
                      </Typography>
                    </Box>
                  )}
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontWeight: 600,
                      fontSize: 17,
                      lineHeight: '20px',
                      letterSpacing: 0,
                      color: '#111927',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minWidth: 0,
                    }}>
                    {formatAmountWithSymbol(row.amount, currency)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Card>
      </Grid>

      <Grid item xs={12} lg={5} sx={{ minWidth: 0, maxWidth: '100%' }}>
        <Card
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            border: '1px solid #E8ECF0',
            height: '100%',
            backgroundImage: 'linear-gradient(135deg, #FFFFFF 0%, #F0F8FD 100%)',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            minWidth: 0,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}>
          <Typography
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontStyle: 'normal',
              fontSize: '20px',
              fontWeight: 600,
              lineHeight: 1,
              letterSpacing: 0,
              color: '#111927',
              mb: 3,
            }}>
            Pipeline Status
          </Typography>

          <Box sx={{ position: 'relative', pl: 0.5, minWidth: 0, width: '100%' }}>
            {displayPipelineData.map((item, index) => {
              const isWarningStatus = ['draft', 'review'].includes(item.status);
              const isErrorStatus = ['expired', 'rejected'].includes(item.status);
              const isLast = index === displayPipelineData.length - 1;
              return (
                <Box
                  key={item.status}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: { xs: 1, sm: 1.5 },
                    pb: isLast ? 0 : { xs: 2.5, sm: 3.5 },
                    position: 'relative',
                    minWidth: 0,
                    width: '100%',
                  }}>
                  {!isLast && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 13,
                        top: 28,
                        bottom: 0,
                        width: 2,
                        background: '#0E719D',
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: '#0E719D',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: 12,
                      fontWeight: 700,
                      flexShrink: 0,
                      zIndex: 1,
                      lineHeight: 1,
                    }}>
                    {index + 1}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, pt: 0.25, overflow: 'hidden' }}>
                    <Typography
                      sx={{
                        fontFamily: 'Roboto, sans-serif',
                        fontStyle: 'normal',
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#111927',
                        lineHeight: 1.35,
                        textTransform: 'uppercase',
                        mb: 0.5,
                        letterSpacing: 0,
                      }}>
                      {(ONBOARDING_DASHBOARD_STATUS[item.status] || item.status)
                        .split(/\s*\/\s*/)
                        .map((part, partIndex, parts) => (
                          <Box
                            key={`${item.status}-${part}`}
                            component="span"
                            sx={{ display: 'block' }}>
                            {part}
                            {partIndex < parts.length - 1 ? ' /' : ''}
                          </Box>
                        ))}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#6C737F',
                        lineHeight: 1.4,
                        whiteSpace: { xs: 'normal', sm: 'normal' },
                        wordBreak: 'break-word',
                      }}>
                      Commitment: {formatCurrency(item.commitmentAmount || 0, currency)}
                      {' • '}
                      Count:&nbsp;{item.count || 0}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                    {isErrorStatus ? (
                      <CancelIcon sx={{ color: '#F04438', fontSize: 24 }} />
                    ) : isWarningStatus ? (
                      <Box
                        component="img"
                        src="/images/warning.svg"
                        alt=""
                        sx={{ width: 21, height: 19, display: 'block' }}
                      />
                    ) : (
                      <Box
                        component="img"
                        src="/images/sent.svg"
                        alt=""
                        sx={{ width: 24, height: 24, display: 'block' }}
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Card>
      </Grid>
    </>
  );
}
